// src/components/desk/primitives.jsx — the Desk's shared class-string primitives
// and dependency-light atoms, extracted VERBATIM from IntakeTab.jsx
// (DESK_MODE_REWRITE_PLAN §3 decomposition, step 1: behavior-preserving split,
// no design changes, no "while I'm here"). These are the tokens the design
// handoff (design/desk-search) builds on — one definition, imported everywhere.

import { createContext, useContext, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '../Icon.jsx'
import { t, tLog } from '../../lib/i18n.js'
import { formatMoney } from '../../lib/money.js'
import { useDeliberateBackdropDismiss } from '../../hooks/useDeliberateBackdropDismiss.js'

export { useDeliberateBackdropDismiss } from '../../hooks/useDeliberateBackdropDismiss.js'

// ─── Card-anchored popover placement (owner ruling 2026-07-25) ────────────────────────────
// THE placement rule for popovers opened from a card footer / chip row. Opens UPWARD by
// preference: these anchors sit on a card's BOTTOM edge, so downward is exactly where the
// viewport runs out first — on a phone the share menu was cropped mid-row, with "link to
// agent" half off the screen. Anchoring the panel's BOTTOM to just above the button also
// means we never need to measure the panel's height, which is content-dependent.
// Falls back downward only when there genuinely isn't room above (a card near the top).
// `maxHeight` is returned too, so a long list scrolls inside the panel instead of running
// off the edge it just avoided — the consumer must pair it with overflow-y-auto.
// Callers close on scroll/resize, so a stale position can't outlive its anchor.
// `prefer` (2026-07-30): the upward default above is the ruling for anchors on a card's
// BOTTOM edge. An anchor in the MIDDLE of a card — the address line's nearby-places hover —
// inverts the reasoning: opening upward there covers the card above, which is the content
// the agent is scanning. Such callers pass 'down' and get the mirror rule (downward unless
// there genuinely isn't room). The default is unchanged, so no existing popover moves.
export function popoverPos(el, width = 320, prefer = 'up') {
  const r = el.getBoundingClientRect()
  const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
  const above = r.top
  const below = window.innerHeight - r.bottom
  const goUp = prefer === 'down'
    ? (below < 200 && above > below)
    : (above >= 200 || above >= below)
  return goUp
    ? { bottom: window.innerHeight - r.top + 4, left, maxHeight: Math.max(140, above - 12) }
    : { top: r.bottom + 4, left, maxHeight: Math.max(140, below - 12) }
}

// ─── "the page moved under it" — and the one scroll that is NOT that (2026-08-09) ─────────
// Every card-anchored popover closes on scroll, because its position was measured once from
// the anchor and re-measuring on each scroll tick is not worth it for a rarely-open panel.
// The listener is on `window` in the CAPTURE phase, and that is what made a scrollable
// popover unscrollable: a scroll INSIDE the panel is dispatched on the panel, capture sees it
// on the way down, and the panel closed on the first wheel notch. Owner-reported on the
// platform-chip hover, whose ad text is routinely longer than the panel is tall.
//
// So the rule is not "close on scroll", it is **close when something OTHER than this panel
// scrolls**. `popoverPos` has always returned a `maxHeight` for exactly this case; pairing it
// with `overflow-y-auto` only became true once the panel was allowed to receive its own
// scroll events.
//
// ⚠️ The guard tests the EVENT TARGET against the panel node, not the pointer position — a
// scroll event carries no coordinates, and an ancestor scroller under the pointer is still
// the page moving. Attach the returned ref to the panel element itself; without the ref the
// hook degrades to the old close-on-any-scroll rather than to never closing.
// `ref` (optional): pass the panel ref a caller already keeps — several of these popovers
// also use it for outside-click detection, and two refs on one node would be a merge helper
// nobody needs. Omit it and the hook owns one.
export function useCloseOnPageMove(active, close, ref = null) {
  const ownRef = useRef(null)
  const panelRef = ref || ownRef
  const closeRef = useRef(close)
  closeRef.current = close   // so the listener is bound once per open, not once per render
  useEffect(() => {
    if (!active) return
    const onScroll = e => {
      const n = panelRef.current
      if (n && e.target instanceof Node && (n === e.target || n.contains(e.target))) return
      closeRef.current()
    }
    const onResize = () => closeRef.current()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [active])
  return panelRef
}

// Does this device actually hover? Touch screens fire a synthetic mouseenter on tap, so a
// hover-only affordance is invisible there — every consumer must offer a tap path instead.
// Moved here from CooperationChips (2026-07-30) when the card-face NearbyHover needed it.
export function useHoverCapable() {
  return useMediaQuery('(hover: hover) and (pointer: fine)')
}

// Hover WITHOUT requiring a fine pointer (2026-08-02). `pointer: fine` describes the
// PRIMARY pointing device, so a touch-capable Windows laptop can report `coarse` while a
// mouse is plugged in and the agent is hovering with it. For an affordance that only has to
// decide "can this person hover at all", that extra condition silently excludes real mouse
// users — the platform-chip comment popover was invisible on exactly such a machine while
// being verifiably correct on every machine that reported `fine`.
//
// Use this when the question is "can they hover"; keep useHoverCapable when the question is
// "is this a precise pointer" (dense hit targets, drag handles).
export function useCanHover() {
  return useMediaQuery('(hover: hover)')
}

function useMediaQuery(q) {
  const [m, setM] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches)
  useEffect(() => {
    const mq = window.matchMedia?.(q)
    if (!mq) return
    const on = e => setM(e.matches)
    setM(mq.matches)   // re-sync: the device may have changed between first paint and effect
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [q])
  return m
}


// `desk-card` is a STYLING HOOK, not decoration: index.css uses it to grow every control inside
// a card to a 44px touch target below the md breakpoint (design 1.4 mobile parity). It rides the
// shared const so a new card surface gets touch parity for free instead of being remembered.
export const card  = 'desk-card bg-card border border-border rounded-xl p-4'
export const input = 'w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'
// Narrow variant for single-digit fields (room/bedroom counts) — same look, less padding.
export const inputCompact = 'w-full rounded-lg border border-input px-2 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'
// Disabled must be visibly OFF, not a 50%-opacity twin of enabled (on the dark theme the two
// were indistinguishable — 2026-07-13 UI audit: "submit button never looks enabled"). A muted
// bg + border swap and not-allowed cursor make valid-vs-blocked readable at a glance.
export const btn = (variant = 'primary') =>
  variant === 'primary'
    ? 'px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium shadow-sm hover:opacity-90 disabled:bg-muted disabled:text-muted-foreground disabled:border disabled:border-border disabled:shadow-none disabled:cursor-not-allowed'
    : variant === 'ghost'
    ? 'px-3 py-1.5 rounded-lg border border-border text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed'
    : variant === 'outline'
    // Neutral bordered button. Was MISSING (2026-07-18): btn('outline') fell through to the
    // destructive branch below, so the zero-result "Search all time" CTA + suggestion chips
    // all rendered alarming red. Red is for real destructive actions only ('destructive').
    ? 'px-4 py-2 rounded-lg border border-border bg-background text-sm font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed'
    : 'px-4 py-2 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
// ─── THE PILL CONTRACT (design/PILL_SYSTEM_SPEC.md §1) ────────────────────────────────────
// One geometry, four states. Before this, ONE column of the lineage modal carried four pill
// grammars (`text-[10px] px-1.5`, `text-[11px] px-2`, `text-xs px-2.5 py-1`, tint sometimes
// border-only and sometimes border+bg), so no two labels aligned.
//
// ⚠️ `box-border` is load-bearing: with content-box a 1px border rendered these at 30.4px
// against a spec of 28. ⚠️ `desk-pill` is a STYLING HOOK, not decoration — index.css uses it
// to opt pills OUT of the `.desk-card button { min-height: 44px }` mobile rule. §3's rule is
// "the pill stays 28px, the ROW around it carries the 44px target"; without the hook every
// pill inflates into a tall near-circle, which is what that rule exists to prevent.
// ⚠️ `flex-none` vs shrinkable — the ONE place this is decided (spec §1 says flex-none; the
// card footer needs its VALUE chips to shrink). Both are right for different jobs, so the
// contract carries both and the call site picks: fixed for affordances (＋, state), shrink for
// value chips whose label ellipsises. Two exclusive classes are never both applied — CSS
// order, not class order, decides a `flex-none`/`shrink` fight, which is a silent bug.
const PILL_BASE = 'desk-pill box-border inline-flex items-center h-7 px-2.5 gap-1.5 rounded-full '
  + 'border text-xs leading-none whitespace-nowrap max-w-full'
const PILL_SM_BASE = 'desk-pill box-border inline-flex items-center h-6 px-2.5 gap-1.5 rounded-full '
  + 'border text-[11.5px] leading-none whitespace-nowrap max-w-full'
export const PILL = `${PILL_BASE} flex-none`
// Header-level state pill, one step down. Used for the ONE pill a card header is allowed (§2).
export const PILL_SM = `${PILL_SM_BASE} flex-none`
// Tint is ALWAYS border + ~10% fill, never border-only — mixing the two is what made six
// identical amber chips read as six different kinds of thing.
export const PILL_NEUTRAL = 'border-border text-foreground/80'
export const PILL_WARN    = 'border-amber-500/45 bg-amber-500/10 text-amber-600 dark:text-amber-300'
export const PILL_OK      = 'border-emerald-500/45 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
export const PILL_INFO    = 'border-sky-500/45 bg-sky-500/10 text-sky-600 dark:text-sky-300'
export const PILL_ADD     = 'border-dashed border-border text-muted-foreground'
// A pill that DOES something gets the hover; a pill that is a FACT must not, or a colleague's
// read-only value looks like a door (the interaction rule the relationship column runs on).
export const PILL_DOOR    = 'hover:border-primary/50 hover:text-foreground cursor-pointer'
// The icon slot is ALWAYS rendered at a fixed width, even when empty — that, not the padding,
// is what makes a stack of labels align.
export const PILL_ICON    = 'inline-flex w-3.5 justify-center flex-none'
// The label itself: truncates, never wraps. `h-7` is fixed, so a wrapped label breaks §1.
export const PILL_LABEL   = 'min-w-0 overflow-hidden text-ellipsis'
// One call for every pill. tone: neutral|warn|ok|info|add. door → interactive (hover+cursor).
// shrink → this pill may ellipsise (value chips); default is fixed-width (affordances).
// tone 'none' = the caller supplies the whole colour set (e.g. VIEWING_STATUS_STYLE). It exists
// so a caller never STACKS its palette on top of a tone: two border-colour classes of equal
// specificity are resolved by stylesheet order, not by the order you wrote them, so
// `pill('neutral') + 'border-blue-600'` is a coin flip. Geometry from here, colour from there.
export const pill = (tone = 'neutral', { door = false, sm = false, shrink = false, extra = '' } = {}) => [
  sm ? PILL_SM_BASE : PILL_BASE,
  shrink ? 'min-w-0' : 'flex-none',
  tone === 'none' ? ''
    : ({ neutral: PILL_NEUTRAL, warn: PILL_WARN, ok: PILL_OK, info: PILL_INFO, add: PILL_ADD }[tone] || PILL_NEUTRAL),
  door ? PILL_DOOR : '', extra,
].filter(Boolean).join(' ')

// ─── A COUNTER is not a pill (documented deviation from spec §4) ────────────────────────────
// `×3`, `+2`, `6/7` render INSIDE a text line, at meta size, aligned to the surrounding
// baseline. Forcing them into the 28px pill would make a superscript-scale annotation the same
// height as the value it annotates — the opposite of what §1 is for. So they get their own
// single definition instead of a fifth ad-hoc literal: four copies of
// `rounded-full px-1.5 py-px text-[10px] bg-muted` existed before this.
export const COUNTER = 'inline-flex items-center rounded-full px-1.5 py-px text-xs leading-none '
  + 'bg-muted text-muted-foreground align-middle'

// Photo-overlay badge — also NOT a pill (second documented deviation). It floats on an image
// (stack count, "N updates"), so it needs an opaque fill for legibility over arbitrary
// photography and must stay small enough not to cover the picture. Sharing §1's transparent
// tints would make it unreadable on a light photo. One definition, five call sites.
export const OVERLAY_BADGE = 'absolute z-10 inline-flex items-center rounded-full px-2 py-0.5 '
  + 'text-xs font-medium leading-none shadow'

// ─── §2 row grammar — a repeated fact is a ROW, not a capsule ──────────────────────────────
// If a section renders the same shape N times (one per agent: cooperations, rent terms,
// meetings), it is a LIST in the specs-column grammar. Six identical amber capsules stacked
// vertically carry no more information than six rows and shout six times as loud.
export const ROW      = 'flex items-center justify-between gap-3 py-1.5 min-h-11 md:min-h-0 border-b border-border/40 last:border-0'
export const ROW_LEFT = 'min-w-0 truncate text-[13.5px] leading-[1.45] text-foreground'
export const ROW_VAL  = 'shrink-0 text-[13.5px] leading-[1.45] text-muted-foreground whitespace-nowrap'
// Section label: 13/500 muted. NEVER `uppercase` — Georgian has no capitals, so uppercasing
// a Georgian label does nothing to it while shouting at the Latin next to it.
export const SECTION_LABEL = 'text-[13px] font-medium tracking-[.02em] text-muted-foreground'

// ⚠️ chip/mobilePill are ALIASES of the contract, deliberately (spec §4): they had their own
// geometry, and leaving them alone is how a fifth grammar would grow back.
export const chip = `${PILL} ${PILL_NEUTRAL} font-medium hover:bg-accent transition-colors`
// Uniform pill for the mobile slider row (2026-07-18 eBay ruling: every pill same size
// and shape; active = filter engaged). flex-none: the row scrolls, pills never shrink.
// Skeleton card (design skin §4): shimmering placeholder blocks (photo, price line, two
// text lines, two chips) — used ONLY under a live loading fact, never as an empty state.
export function SkeletonCard() {
  return (
    <div className={`${card} animate-pulse`}>
      <div className="flex gap-2">
        <div className="w-28 h-24 rounded-lg bg-muted shrink-0" />
        <div className="flex-1 space-y-2 py-1 min-w-0">
          <div className="h-4 w-2/5 rounded bg-muted" />
          <div className="h-3 w-4/5 rounded bg-muted" />
          <div className="h-3 w-3/5 rounded bg-muted" />
          <div className="flex gap-1.5 pt-1">
            <div className="h-4 w-14 rounded-full bg-muted" />
            <div className="h-4 w-14 rounded-full bg-muted" />
          </div>
        </div>
      </div>
    </div>
  )
}

// A shimmer that never changes is indistinguishable from a hang (2026-08-10). Reported as
// "the filter is stuck on a blank page and never retrieves results" — it was not stuck: on an
// admin account a filtered leads search takes ~30s cold, and six identical placeholder cards
// say nothing about whether anything is still happening. So after DELAY_S the grid grows one
// line that counts. It claims NOTHING it cannot know — no percentage, no estimate, no "almost
// there": just that the search is still running and how long it has been. Mounted alongside
// the skeletons and unmounted with them, so the timer's life is exactly the wait's.
export function SearchElapsedNote({ delayS = 5 }) {
  const [secs, setSecs] = useState(0)
  useEffect(() => {
    const t0 = Date.now()
    const id = setInterval(() => setSecs(Math.floor((Date.now() - t0) / 1000)), 1000)
    return () => clearInterval(id)
  }, [])
  if (secs < delayS) return null
  return (
    <p className="col-span-full text-xs text-muted-foreground">
      {tLog('desk_search_still_running', { n: secs })}
    </p>
  )
}

export const mobilePill = active => `${chip} ${active ? 'border-primary text-foreground' : 'text-muted-foreground'}`

export function Badge({ label, variant = 'default', title }) {
  const cls = {
    default:  'bg-muted text-muted-foreground',
    blue:     'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    green:    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    red:      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    yellow:   'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    amber:    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  }[variant] || 'bg-muted text-muted-foreground'
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${cls}`} title={title}>{label}</span>
}

// Part D (2026-07-14): price/commission change indicator on a published offer.
// Actual old→new values shown to EVERY agent (full transparency is the deterrent
// — user decision); count+history come from the server's event-derived audit
// trail. Visibility only: ordering stays cheapest-first, never touched by this.
export function PriceHistoryBadge({ count, history }) {
  if (!count) return null
  const lines = (history || []).map(h => {
    const bits = []
    if (h.from_price !== h.to_price || h.from_currency !== h.to_currency)
      bits.push(`${h.from_price} ${h.from_currency} → ${h.to_price} ${h.to_currency}`)
    if (h.from_commission !== h.to_commission)
      bits.push(`${h.from_commission ?? '—'}% → ${h.to_commission ?? '—'}%`)
    const when = h.at ? new Date(h.at).toLocaleDateString() : ''
    return `${bits.join(' · ')}${when ? ` (${when})` : ''}`
  })
  return (
    <span className="rounded-full bg-amber-500/15 text-amber-500 px-1.5 py-0.5 font-semibold cursor-help"
      title={lines.join('\n')}><Icon name="arrow-up-down" /> ×{count}</span>
  )
}

// Display currency for every price the Desk prints — the top-bar toggle's pick plus
// the shared NBG rate. A context rather than a prop chain because prices render at a
// dozen depths (card face, stack header, sibling rows, modals) and threading two more
// props through all of them would guarantee one surface silently drifts to the old
// "show whatever the ad said" behaviour. Default is inert: no rate = no conversion.
export const MoneyCtx = createContext({ display: null, rate: null })
export const useMoney = () => useContext(MoneyCtx)

// The ad's own text, keyed by listing_id, for the platform-chip hover (2026-07-30).
// A CONTEXT rather than a prop for the same reason MoneyCtx is one: the chips render
// several components deep and behind memo boundaries, and threading a fresh object
// through HistoryGroup/HistoryItem on every fetch is exactly how the Desk's render
// identity contract gets broken silently. Default empty = no tooltips, never a crash.
export const AdTextCtx = createContext({})
export const useAdText = () => useContext(AdTextCtx)

// Who is looking — the acting agent's roster id, for the handful of controls that WRITE
// from deep inside a card (today: the author-is-an-agent vote in the contact popover,
// 0374). A context for the same reason the two above are: the popover renders four
// components down and behind memo boundaries, and threading an identity prop through
// HistoryGroup/HistoryItem/CooperationChips is how the Desk's render identity contract
// gets broken silently. Default null is inert — no identity, no vote control, never a
// crash: the server refuses an unidentified write anyway, so the UI simply doesn't offer it.
export const ViewerCtx = createContext({ agentId: null })
export const useViewer = () => useContext(ViewerCtx)

// Tooltip for a summarize() price object: why it's approximate, and/or why it's muted.
// Both reasons can apply to the same price, so they compose rather than override.
// ONE formatter for observed price moves, shared by the badge below and the price
// tooltip above it (2026-07-30). Two renderings of the same history that drifted apart
// would be the "all three surfaces agree" promise in the badge's own header, broken.
export const priceHistoryLines = history => (history || []).map(h =>
  `${Number(h.from).toLocaleString('en-US')} → ${Number(h.to).toLocaleString('en-US')} ${h.currency || ''}`
  + (h.pct != null ? ` (${h.pct > 0 ? '+' : ''}${h.pct}%)` : '')
  + (h.observed_at ? ` · ${new Date(h.observed_at).toLocaleDateString()}` : ''))

export const priceTitle = p => [
  p?.approx ? tLog('desk_price_observed_as', { observed: p.observed }) : null,
  // 2026-07-30: when the price is NOT converted there was no tooltip at all, so a
  // USD-displayed USD ad answered nothing on hover. `counterpart` is the same amount in
  // the other currency (never set alongside `observed`, which already says it).
  p?.counterpart || null,
  p?.muted ? t('desk_entry_price_inherited') : null,
  // Price history on the PRICE itself (owner request 2026-07-30), not only on the little
  // badge beside it — the price is what the eye lands on, and the moves were one pixel
  // target away. Newline-joined so the transitions stack; the reasons above stay ' · '.
  ...(p?.history?.length ? [priceHistoryLines(p.history).join('\n')] : []),
].filter(Boolean).join(' · ') || undefined

// The one place a price becomes text. `approx` prices carry the observed value in
// their tooltip so the conversion is never mistaken for what the ad actually says.
export function Money({ amount, currency, suffix = '', className = '', title: extraTitle }) {
  const { display, rate } = useMoney()
  const m = formatMoney(amount, currency, display, rate, { suffix })
  if (!m) return null
  const tip = [m.approx ? tLog('desk_price_observed_as', { observed: m.observed }) : null,
    m.counterpart || null, extraTitle]
    .filter(Boolean).join(' · ') || undefined
  return <span className={className} title={tip}>{m.text}</span>
}

export const glowAmber = 'border-amber-400 ring-2 ring-amber-400/60 shadow-[0_0_14px_rgba(251,191,36,0.55)] animate-pulse'
export const glowBlue  = 'border-sky-400 ring-2 ring-sky-400/50 shadow-[0_0_10px_rgba(56,189,248,0.4)]'

// Crawler-observed price moves on a scraped-listing card face (2026-07-20 owner request):
// hover the badge next to the price to see the full history. Rows come from the server's
// agent-listings price_history (0022 observation diffs, already normalized into the
// listing's own currency by classifyPriceChange) — same data the lineage modal's PRICE
// HISTORY section and the notifications bell show, so all three surfaces agree.
export function ObservedPriceHistoryBadge({ history }) {
  if (!history?.length) return null
  const lines = priceHistoryLines(history)
  const last = history[history.length - 1]
  const down = last?.direction === 'down'
  return (
    <span onClick={e => e.stopPropagation()}
      className={`ml-1.5 align-middle text-xs font-semibold rounded-full px-1.5 py-0.5 cursor-help ${down ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-500'}`}
      title={lines.join('\n')}>
      {down ? <Icon name="trending-down" /> : <Icon name="trending-up" />}
      {/* Designer board 2026-07-23: the LATEST move's % is card-visible ("↓ 5.9%"),
          not hover-only; ×N count rides along only when there were several moves. */}
      {last?.pct != null ? ` ${Math.abs(last.pct)}%` : null}
      {history.length > 1 ? ` ×${history.length}` : (last?.pct == null ? ` ×${history.length}` : null)}
    </span>
  )
}

// Mobile-safe button label (owner rule 2026-07-20: icon-only on phones, words from sm: up).
// `icon` is an Icon.jsx name — the ONE icon system (Lucide paths inline; see Icon.jsx for
// why not the npm package). Any leading emoji/text glyph in the i18n string is stripped so
// a label like "✎ რედაქტირება" renders as the SVG pencil plus the word, never both glyphs.
// Tooltips carry the full wording, so the icon-only state is never a dead end.
export function BtnLabel({ label, icon }) {
  const words = String(label).replace(/^[^\p{L}\p{N}]+\s*/u, '')
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon name={icon} size={14} />
      <span className="hidden sm:inline">{words}</span>
    </span>
  )
}

// timeAgo — moved VERBATIM from IntakeTab.jsx (plan §3 decomposition).
// Translated 2026-08-08: the four forms were the last hardcoded English on an otherwise
// all-Georgian Desk, and because this is a shared primitive one string leaked onto every
// surface that renders a timestamp (match rows, meeting cards, listing ages, verdict lines).
// ⚠️ PAST-ONLY, unchanged: a future timestamp makes `s` negative and falls into the first
// branch, so an upcoming appointment would read "just now". Callers with future dates must
// render the appointment TIME instead — NotificationsPanel's `tier === 'upcoming'` row does.
export function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return t('desk_time_just_now')
  const m = Math.floor(s / 60); if (m < 60) return tLog('desk_time_min_ago', { n: m })
  const h = Math.floor(m / 60); if (h < 24) return tLog('desk_time_hour_ago', { n: h })
  return tLog('desk_time_day_ago', { n: Math.floor(h / 24) })
}

// Modal — moved VERBATIM from IntakeTab.jsx (plan §3 decomposition).
// ─── Generic modal chrome (Client / Property use this; Meeting / Candidate
// render their own existing full-screen modals and are NOT wrapped in it) ───
// A backdrop dismissal is deliberate only when BOTH ends of the gesture land on the
// backdrop. React's synthetic `click` can target the backdrop after a press begins inside
// the modal and the pointer slips outside before release; closing on that click discards
// in-progress form work. Pointer down arms the backdrop, pointer up consumes that exact arm.
// `headerRight` — a slot beside the ✕, for controls that belong at the top-right CORNER of the
// sheet rather than in the action row (owner 2026-09-10, after seeing both placements live).
// ⚠️ It sits BEFORE the close button and `shrink-0` keeps the ✕ from being squeezed off a
// narrow sheet: the close control is the one affordance a modal may never lose, and on mobile
// the header is the tightest row in the app.
export function Modal({ title, subtitle, onClose, children, wide = false, headerRight = null, headerBelow = null }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  // ── Dialog semantics + focus containment (review 2026-09-17: Shift+Tab from ✕ focused the page behind) ──
  // Focus moves into the dialog on open, Tab / Shift+Tab wrap inside it, and focus returns to what opened it on
  // close. Only Tab is handled — clicks, popovers portaled elsewhere and nested modals are untouched: a nested
  // modal is its own dialog and keydown only reaches the one that holds focus.
  const dialogRef = useRef(null)
  const titleId = useId()
  useEffect(() => {
    const opener = document.activeElement
    const el = dialogRef.current
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true })
    return () => { if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus({ preventScroll: true }) }
  }, [])
  const onDialogKeyDown = e => {
    if (e.key !== 'Tab' || !dialogRef.current) return
    const f = [...dialogRef.current.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
      .filter(n => n.offsetParent !== null || n === document.activeElement)
    if (!f.length) { e.preventDefault(); return }
    const first = f[0], last = f[f.length - 1]
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }
  // bg-black/50 + backdrop-blur-sm barely dents a bright photo thumbnail behind it (a 50%
  // black tint over an already-dark theme reads as "not covering" against something like a
  // blue-sky card image) — darkened + blurred more so busy card grids are properly obscured.
  //
  // Portaled to document.body (2026-07-05): rendered inline, IntakeTab's root div acts as a
  // fixed-position containing block (measured: a position:fixed inset-0 child of it lands at
  // top=24 with a shortened height, leaving the top strip of the viewport uncovered — the
  // "blur doesn't reach the header" bug). None of the usual suspects (transform/filter/
  // contain/container-type) read on it, so instead of chasing the property, escape the
  // subtree entirely — body-level fixed measures a full 0/0/100%/100%.
  return createPortal(
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md ${wide ? 'p-0 lg:p-4' : 'p-4'}`}
      {...backdropDismiss}>
      {/* wide (§9 lineage redesign, 2026-07-23): opt-in two-column width — every other
          consumer keeps the max-w-2xl default.
          ⚠️ HARD RULE below lg (designer fix 1, 2026-07-23): a wide modal is FULL-VIEWPORT,
          single column, overflow-x-hidden — never the desktop board squeezed into a phone
          with a horizontal scrollbar. Full height too: an edge-to-edge sheet, so the §9
          sticky footer sits on the real bottom instead of floating mid-air. */}
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onDialogKeyDown}
        className={`bg-card border border-border w-full overflow-y-auto overflow-x-hidden shadow-xl outline-none ${wide
        ? 'h-full max-h-full rounded-none lg:h-auto lg:max-h-[90vh] lg:rounded-xl lg:max-w-5xl'
        : 'rounded-xl max-w-2xl max-h-[90vh]'}`}>
        {/* z-10: the body's positioned content (photo carousel is relative) otherwise paints
            OVER this sticky bar when the modal scrolls — both were z-auto, DOM order won. */}
        {/* 🔴 z-20, AND THE NUMBER IS THE FIX (owner-reported on mobile 2026-09-10). At z-10 this
            bar TIED with the lineage modal's image-set tab overlay, which is also z-10 — and a
            tie is resolved by DOM ORDER, so the gallery, being later, painted its pills straight
            over the address and price. That is the identical failure the note below already
            records ("both were z-auto, DOM order won"); raising the header to z-10 fixed it once,
            and a later overlay matched the number and recreated it.
            📌 So the rule, not the number: THE CHROME MUST OUTRANK THE CONTENT, and content
            overlays live at z-10 or below. Raising the header rather than lowering that one
            overlay fixes every content overlay in every modal at once — the alternative repairs
            one caller and leaves the next equal-z overlay to find the same bug again.
            ⚠️ An overlay INSIDE a z-10 container (ImageSetTabs' own z-20/z-30 fades and arrows)
            is contained by that container's stacking context and cannot reach the header. Only a
            SIBLING of the header at z >= 20 could, so do not add one. */}
        <div className="px-5 py-2 border-b border-border sticky top-0 bg-card z-20">
          {/* TWO ROWS, and the split is the point (owner on mobile 2026-09-10: "address is
              compacted too much — move the address line below as it needs maximum width and put
              the rest on top side by side"). One row made the address share its width with the
              price, the ticker, the freshness chip AND three buttons, so on a phone it wrapped to
              two cramped lines while the row's right half sat half empty. The address is the
              longest and least abbreviatable thing here — the answer to "what am I looking at" —
              so it gets its own full-width line and everything short shares the top.
              ⚠️ headerBelow renders only when given, so every other modal keeps ONE row and its
              current look. */}
          <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            {/* ⚠️ A `wide` modal's title is rich JSX (address + badge row), and `truncate`
                carries whitespace-nowrap — at 390px the badges wrapped to a second line and
                the h2 CLIPPED them (measured 2026-07-24: a freshness badge 56px past the
                sheet's edge, invisible, no scrollbar to reveal it). Below lg a wide title
                wraps; from lg up it truncates as before. Narrow modals keep the old behavior —
                their titles are plain strings, where one ellipsised line is correct. */}
            <h2 id={titleId} className={`text-sm font-semibold ${wide ? 'lg:truncate' : 'truncate'}`}>{title}</h2>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <span className="flex items-center gap-1 shrink-0">
            {headerRight}
            <button type="button" onClick={onClose} aria-label={t('close')} title={t('close')}
              className="text-muted-foreground hover:text-foreground text-lg leading-none shrink-0
                         min-w-11 min-h-11 flex items-center justify-center"><span aria-hidden="true">✕</span></button>
          </span>
          </div>
          {headerBelow && <div className="mt-1">{headerBelow}</div>}
        </div>
        {/* 🔴 `isolate` IS THE FIX THAT ENDS THE ARMS RACE (2026-09-10, third z-index report in
            one day). Raising the header to z-20 beat the tab overlay's z-10 and immediately TIED
            with the gallery's zoom ＋ (also z-20), which then floated over the address — one bug
            traded for another. The gallery also carries a z-[400] arrow, so no header number is
            safe: content will always out-number chrome eventually.
            CSS isolation:isolate makes this body its own stacking context, so EVERY z inside it —
            20, 400, anything — resolves WITHIN the body and cannot reach a sibling. The header's
            z-20 now beats the body as a whole, once, permanently. */}
        <div className="p-5 isolate">{children}</div>
      </div>
    </div>,
    document.body
  )
}
